import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Analytics.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Analytics - Platform-wide analytics and reports
 */
function Analytics() {
    const [analytics, setAnalytics] = useState({
        userGrowth: [],
        revenueData: [],
        storesByPackage: [],
        topPerformingStores: [],
        subscriptionTrends: [],
        platformHealth: {}
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState('30days'); // 7days, 30days, 90days, 1year

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange]);

    const fetchAnalytics = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.get(`${BASEURL}superadmin/analytics/${userId}`, {
                headers: { token },
                params: { range: dateRange }
            });

            if (response.data.success) {
                setAnalytics(response.data.data);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError('Failed to load analytics data');
            setLoading(false);
        }
    };

    const getHealthStatus = (health) => {
        if (health >= 90) return { label: 'Excellent', color: '#008060', icon: 'fa-check-circle' };
        if (health >= 70) return { label: 'Good', color: '#2c6ecb', icon: 'fa-check' };
        if (health >= 50) return { label: 'Fair', color: '#f59e0b', icon: 'fa-exclamation-triangle' };
        return { label: 'Poor', color: '#dc2626', icon: 'fa-times-circle' };
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading analytics...
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className={styles.error}>
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            </Layout>
        );
    }

    const { platformHealth } = analytics;
    const healthStatus = getHealthStatus(platformHealth.score || 0);

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h1>Platform Analytics</h1>
                        <p>Insights and performance metrics</p>
                    </div>
                    <div className={styles.dateRangeSelector}>
                        <button
                            className={dateRange === '7days' ? styles.active : ''}
                            onClick={() => setDateRange('7days')}
                        >
                            7 Days
                        </button>
                        <button
                            className={dateRange === '30days' ? styles.active : ''}
                            onClick={() => setDateRange('30days')}
                        >
                            30 Days
                        </button>
                        <button
                            className={dateRange === '90days' ? styles.active : ''}
                            onClick={() => setDateRange('90days')}
                        >
                            90 Days
                        </button>
                        <button
                            className={dateRange === '1year' ? styles.active : ''}
                            onClick={() => setDateRange('1year')}
                        >
                            1 Year
                        </button>
                    </div>
                </div>

                {/* Platform Health */}
                <div className={styles.healthSection}>
                    <div className={styles.healthCard}>
                        <div className={styles.healthIcon} style={{ color: healthStatus.color }}>
                            <i className={`fas ${healthStatus.icon}`}></i>
                        </div>
                        <div className={styles.healthInfo}>
                            <h2>Platform Health</h2>
                            <div className={styles.healthScore}>
                                <span style={{ color: healthStatus.color }}>
                                    {platformHealth.score || 0}%
                                </span>
                                <span className={styles.healthLabel}>{healthStatus.label}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.healthMetrics}>
                        <div className={styles.metric}>
                            <i className="fas fa-server"></i>
                            <div>
                                <strong>{platformHealth.uptime || 0}%</strong>
                                <span>Uptime</span>
                            </div>
                        </div>
                        <div className={styles.metric}>
                            <i className="fas fa-tachometer-alt"></i>
                            <div>
                                <strong>{platformHealth.avgResponseTime || 0}ms</strong>
                                <span>Avg Response Time</span>
                            </div>
                        </div>
                        <div className={styles.metric}>
                            <i className="fas fa-users"></i>
                            <div>
                                <strong>{platformHealth.activeUsers || 0}</strong>
                                <span>Active Users Today</span>
                            </div>
                        </div>
                        <div className={styles.metric}>
                            <i className="fas fa-shopping-cart"></i>
                            <div>
                                <strong>{platformHealth.ordersToday || 0}</strong>
                                <span>Orders Today</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key Metrics Grid */}
                <div className={styles.metricsGrid}>
                    {/* User Growth */}
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <h3>
                                <i className="fas fa-user-plus"></i>
                                User Growth
                            </h3>
                            <span className={styles.growthBadge}>
                                +{analytics.userGrowth?.growth || 0}%
                            </span>
                        </div>
                        <div className={styles.chartBody}>
                            <div className={styles.placeholder}>
                                <i className="fas fa-chart-line"></i>
                                <p>User growth visualization</p>
                                <span>{analytics.userGrowth?.totalNewUsers || 0} new users</span>
                            </div>
                        </div>
                    </div>

                    {/* Revenue Trends */}
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <h3>
                                <i className="fas fa-dollar-sign"></i>
                                Revenue Trends
                            </h3>
                            <span className={styles.revenueBadge}>
                                ৳{analytics.revenueData?.total?.toLocaleString() || 0}
                            </span>
                        </div>
                        <div className={styles.chartBody}>
                            <div className={styles.placeholder}>
                                <i className="fas fa-chart-area"></i>
                                <p>Revenue trend visualization</p>
                                <span>Average: ৳{analytics.revenueData?.average?.toLocaleString() || 0}/month</span>
                            </div>
                        </div>
                    </div>

                    {/* Stores by Package */}
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <h3>
                                <i className="fas fa-box"></i>
                                Package Distribution
                            </h3>
                        </div>
                        <div className={styles.chartBody}>
                            <div className={styles.packageDistribution}>
                                {analytics.storesByPackage?.map((pkg, index) => (
                                    <div key={index} className={styles.packageItem}>
                                        <div className={styles.packageBar}>
                                            <div
                                                className={styles.packageBarFill}
                                                style={{
                                                    width: `${pkg.percentage || 0}%`,
                                                    background: ['#008060', '#2c6ecb', '#f59e0b', '#dc2626'][index % 4]
                                                }}
                                            ></div>
                                        </div>
                                        <div className={styles.packageLabel}>
                                            <span>{pkg.name}</span>
                                            <strong>{pkg.count} ({pkg.percentage}%)</strong>
                                        </div>
                                    </div>
                                ))}
                                {(!analytics.storesByPackage || analytics.storesByPackage.length === 0) && (
                                    <div className={styles.noData}>No package data available</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Subscription Trends */}
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <h3>
                                <i className="fas fa-credit-card"></i>
                                Subscription Status
                            </h3>
                        </div>
                        <div className={styles.chartBody}>
                            <div className={styles.subscriptionGrid}>
                                <div className={`${styles.subCard} ${styles.trial}`}>
                                    <strong>{analytics.subscriptionTrends?.trial || 0}</strong>
                                    <span>Trial</span>
                                </div>
                                <div className={`${styles.subCard} ${styles.active}`}>
                                    <strong>{analytics.subscriptionTrends?.active || 0}</strong>
                                    <span>Active</span>
                                </div>
                                <div className={`${styles.subCard} ${styles.expired}`}>
                                    <strong>{analytics.subscriptionTrends?.expired || 0}</strong>
                                    <span>Expired</span>
                                </div>
                                <div className={`${styles.subCard} ${styles.suspended}`}>
                                    <strong>{analytics.subscriptionTrends?.suspended || 0}</strong>
                                    <span>Suspended</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Performing Stores */}
                <div className={styles.topStoresSection}>
                    <h2>
                        <i className="fas fa-trophy"></i>
                        Top Performing Stores
                    </h2>
                    <div className={styles.topStoresGrid}>
                        {analytics.topPerformingStores?.slice(0, 5).map((store, index) => (
                            <div key={store._id} className={styles.topStoreCard}>
                                <div className={styles.rank}>#{index + 1}</div>
                                <div className={styles.storeInfo}>
                                    <h4>{store.storeName}</h4>
                                    <p>{store.domain}</p>
                                </div>
                                <div className={styles.storeMetrics}>
                                    <div className={styles.storeMetric}>
                                        <i className="fas fa-shopping-cart"></i>
                                        <span>{store.totalOrders || 0} orders</span>
                                    </div>
                                    <div className={styles.storeMetric}>
                                        <i className="fas fa-dollar-sign"></i>
                                        <span>৳{store.totalRevenue?.toLocaleString() || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {(!analytics.topPerformingStores || analytics.topPerformingStores.length === 0) && (
                            <div className={styles.noTopStores}>
                                <i className="fas fa-inbox"></i>
                                <p>No performance data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Analytics;
